import React, { useImperativeHandle, forwardRef, useState } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControl,
  InputLabel,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';

import SelectInput from '../../Inputs/SelectInput';
import TextInput from '../../Inputs/TextInput';

export const OUTRO_MOTIVO = 'OUTRO_MOTIVO';

const OPCAO_OUTRO_MOTIVO = {
  label: 'Outro motivo (digitar)',
  value: OUTRO_MOTIVO,
};

// Mesmos estilos do DialogoConfirmacao, trocando o ícone de pergunta por um de
// alerta: aqui a confirmação dispara um ato irreversível na SEFAZ.
const useStyles = makeStyles((theme) => ({
  paper: {
    borderRadius: 16,
    padding: theme.spacing(1),
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: theme.spacing(3),
  },
  iconContainer: {
    backgroundColor: 'rgba(244, 143, 66, 0.1)',
    borderRadius: '50%',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  icon: {
    fontSize: 40,
    color: '#F48F42',
  },
  title: {
    fontWeight: 'bold',
    color: '#333',
    textAlign: 'center',
  },
  content: {
    textAlign: 'center',
    padding: theme.spacing(1, 4, 2),
  },
  actions: {
    justifyContent: 'center',
    padding: theme.spacing(2, 3, 3),
    gap: theme.spacing(2),
  },
  btnConfirmar: {
    borderRadius: 8,
    padding: '8px 30px',
    backgroundColor: '#009FD4',
    color: 'white',
    fontWeight: 'bold',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#007ca5',
    },
  },
  btnCancelar: {
    borderRadius: 8,
    padding: '8px 30px',
    fontWeight: 'bold',
    textTransform: 'none',
    color: '#757575',
  },
}));

/**
 * Confirmação que exige uma justificativa de texto — o equivalente do
 * DialogoConfirmacao para atos que precisam de motivo registrado.
 *
 * Mesmo contrato de ref do DialogoConfirmacao:
 *   ref.current.handleOpen(titulo, conteudo, codigo)
 * mas `handleConfirma` recebe `(codigo, justificativa)`.
 *
 * As opções prontas existem para que o usuário quase nunca esbarre no mínimo
 * de caracteres — todas já passam do limite exigido.
 */
const DialogoJustificativa = forwardRef(
  (
    {
      handleConfirma,
      handleRejeita = () => {},
      opcoes = [],
      tamanhoMinimo = 15,
      tamanhoMaximo = 255,
      rotuloCampo = 'Justificativa',
    },
    ref,
  ) => {
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [codigo, setCodigo] = useState(0);
    const [opcaoSelecionada, setOpcaoSelecionada] = useState('');
    const [textoLivre, setTextoLivre] = useState('');

    // O escape para texto livre é do próprio diálogo: quem chama passa só as
    // sugestões do domínio.
    const opcoesComTextoLivre =
      opcoes.length > 0 ? [...opcoes, OPCAO_OUTRO_MOTIVO] : [];

    const usaTextoLivre =
      opcaoSelecionada === OUTRO_MOTIVO || opcoes.length === 0;
    const justificativa = usaTextoLivre ? textoLivre : opcaoSelecionada;
    const justificativaLimpa = justificativa.trim();

    function getError() {
      if (!justificativaLimpa) return 'Este campo é obrigatório';
      if (justificativaLimpa.length < tamanhoMinimo) {
        return `Mínimo de ${tamanhoMinimo} caracteres (exigência da SEFAZ)`;
      }
      if (justificativaLimpa.length > tamanhoMaximo) {
        return `Máximo de ${tamanhoMaximo} caracteres`;
      }
      return '';
    }

    const error = getError();

    useImperativeHandle(ref, () => ({
      handleOpen(titleNew, contentNew, codigoNew) {
        setTitle(titleNew);
        setContent(contentNew);
        setCodigo(codigoNew);
        setOpcaoSelecionada(opcoes.length > 0 ? opcoes[0].value : '');
        setTextoLivre('');
        setOpen(true);
      },
    }));

    const handleClose = () => {
      setOpen(false);
      handleRejeita(codigo);
    };

    const handleSalvar = () => {
      if (error !== '') return;
      setOpen(false);
      handleConfirma(codigo, justificativaLimpa);
    };

    return (
      <Dialog
        open={open}
        onClose={handleClose}
        classes={{ paper: classes.paper }}
        disableBackdropClick
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <Box className={classes.header}>
          <div className={classes.iconContainer}>
            <ReportProblemOutlinedIcon className={classes.icon} />
          </div>
          <Typography variant="h6" className={classes.title}>
            {title || 'Confirmação'}
          </Typography>
        </Box>

        <DialogContent className={classes.content}>
          <DialogContentText style={{ color: '#666' }}>
            {content}
          </DialogContentText>

          {opcoes.length > 0 ? (
            <FormControl variant="outlined" fullWidth style={{ marginTop: 16 }}>
              <InputLabel shrink id="label-justificativa">
                {rotuloCampo}
              </InputLabel>
              <SelectInput
                labelId="label-justificativa"
                name="label-justificativa"
                label={rotuloCampo}
                handleEnter={() => {}}
                handleKey={() => {}}
                value={opcaoSelecionada}
                onChange={(e) => setOpcaoSelecionada(e.target.value)}
                options={opcoesComTextoLivre}
              />
            </FormControl>
          ) : null}

          {usaTextoLivre ? (
            <TextInput
              fullWidth
              multiline
              rows={3}
              label={rotuloCampo}
              value={textoLivre}
              onChange={(e) => setTextoLivre(e.target.value)}
              handleEnter={() => {}}
              handleKey={() => {}}
              error={error !== ''}
              helperText={
                error !== ''
                  ? error
                  : `${justificativaLimpa.length}/${tamanhoMaximo}`
              }
            />
          ) : null}
        </DialogContent>

        <DialogActions className={classes.actions}>
          <Button
            onClick={handleClose}
            className={classes.btnCancelar}
            tabIndex={-1}
          >
            Não
          </Button>
          <Button
            onClick={handleSalvar}
            variant="contained"
            className={classes.btnConfirmar}
            disabled={error !== ''}
            tabIndex={-1}
          >
            Sim
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

export default DialogoJustificativa;
